import { initializeApp, FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  Auth,
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  User
} from 'firebase/auth';

// Firebase app and auth instances  
let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let initPromise: Promise<void> | null = null;

// Initialize Firebase with config from server
async function initFirebase(): Promise<void> {
  if (app && auth) return;
  
  if (initPromise) {
    await initPromise;
    return;
  }
  
  initPromise = (async () => {
    try {
      const response = await fetch('/api/firebase-config');
      const firebaseConfig = await response.json();
      
      app = initializeApp(firebaseConfig);
      auth = getAuth(app);
    } catch (error) {
      console.error('Failed to initialize Firebase:', error);
      throw error;
    }
  })();
  
  await initPromise;
}

// Ensure Firebase is initialized before using auth
const getFirebaseAuth = async (): Promise<Auth> => {
  await initFirebase();
  if (!auth) {
    throw new Error('Firebase auth not initialized');
  }
  return auth;
};

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Authentication functions
export const signInWithEmail = async (email: string, password: string) => {
  const authInstance = await getFirebaseAuth();
  return signInWithEmailAndPassword(authInstance, email, password);
};

export const signUpWithEmail = async (email: string, password: string, firstName?: string, lastName?: string) => {
  const authInstance = await getFirebaseAuth();
  const userCredential = await createUserWithEmailAndPassword(authInstance, email, password);
  if (firstName || lastName) {
    await updateProfile(userCredential.user, {
      displayName: `${firstName || ''} ${lastName || ''}`.trim()
    });
  }
  return userCredential;
};

export const signInWithGoogle = async () => {
  const authInstance = await getFirebaseAuth();
  return signInWithPopup(authInstance, googleProvider);
};

export const logOut = async () => {
  const authInstance = await getFirebaseAuth();
  return signOut(authInstance);
};

export const resetPassword = async (email: string) => {
  const authInstance = await getFirebaseAuth();
  return sendPasswordResetEmail(authInstance, email);
};

// Get current user token
export const getIdToken = async () => {
  const authInstance = await getFirebaseAuth();
  const user = authInstance.currentUser;
  if (user) {
    return await user.getIdToken();
  }
  return null;
};

// Auth state listener
export const onAuthChange = async (callback: (user: User | null) => void) => {
  const authInstance = await getFirebaseAuth();
  return onAuthStateChanged(authInstance, callback);
};

export { initFirebase, getFirebaseAuth };
export default auth;